import { z } from "zod/v4";

import { ToolExecutionResultSchema } from "./nodes/tool-execution-result";

export const UIMessageStreamPartSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("workflow-start"),
    workflowId: z.string(),
    workflowName: z.string().optional(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("workflow-finish"),
    workflowId: z.string(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("text"),
    text: z.string(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("error"),
    errorText: z.string(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("tool-input-start"),
    toolCallId: z.string(),
    toolName: z.string(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("tool-group"),
    toolCallId: z.string(),
    toolName: z.string(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
    parts: z.lazy(() => z.array(UIMessageStreamPartSchema)),
  }),
  z.object({
    type: z.literal("tool-input-delta"),
    toolCallId: z.string(),
    inputTextDelta: z.string(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("tool-input-available"),
    toolCallId: z.string(),
    toolName: z.string(),
    input: z.unknown(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("tool-output-available"),
    toolCallId: z.string(),
    output: ToolExecutionResultSchema,
    providerMetadata: z.any().optional(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("reasoning"),
    text: z.string(),
    providerMetadata: z.record(z.any(), z.any()).optional(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("reasoning-part-finish"),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("source-url"),
    sourceId: z.string(),
    url: z.string(),
    title: z.string().optional(),
    providerMetadata: z.any().optional(), // Use z.any() for generic metadata
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("source-document"),
    sourceId: z.string(),
    mediaType: z.string(),
    title: z.string(),
    filename: z.string().optional(),
    providerMetadata: z.any().optional(), // Use z.any() for generic metadata
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("file"),
    url: z.string(),
    mediaType: z.string(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("start-step"),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("finish-step"),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("start"),
    messageMetadata: z.unknown().optional(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("finish"),
    messageMetadata: z.unknown().optional(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
  z.object({
    type: z.literal("message-metadata"),
    messageMetadata: z.unknown(),
    messageId: z.string(),
    timestamp: z.number(),
    nodeId: z.string(),
  }),
]);

export type UIMessageStreamPart = z.infer<typeof UIMessageStreamPartSchema>;
